import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Package, Archive, PieChart, Upload, Calendar } from 'lucide-react';
import { useLanguage } from '@/contexts/LanguageContext';
import AutoHideSidebar from '@/components/AutoHideSidebar';
import { ROUTES } from '@/lib/routes';

interface ModuleItem {
  id: string;
  titleKey: string;
  icon: React.ComponentType<any>;
  descriptionKey: string;
  badge?: string;
  route: string;
}

const ProductOverview = () => {
  const [userEmail, setUserEmail] = useState('');
  const [companyName, setCompanyName] = useState('');
  const navigate = useNavigate();
  const { t } = useLanguage();

  const modules: ModuleItem[] = [
    {
      id: 'product-mgmt',
      titleKey: 'productMgmt',
      icon: Package,
      descriptionKey: 'productMgmt',
      route: '/product-overview/management'
    },
    {
      id: 'stock-mgmt',
      titleKey: 'inventoryMgmt',
      icon: Archive,
      descriptionKey: 'inventoryMgmt',
      route: '/product-overview/stock'
    },
    {
      id: 'product-stats',
      titleKey: 'productStats',
      icon: PieChart,
      descriptionKey: 'productStats',
      route: '/product-overview/statistics'
    },
    {
      id: 'product-import',
      titleKey: 'productImport',
      icon: Upload,
      descriptionKey: 'productImport',
      route: '/product-overview/import'
    }
    ,
    {
      id: 'product-stock-import',
      titleKey: 'productStockImport',
      icon: Upload,
      descriptionKey: 'productStockImport',
      route: '/product-overview/import-stock'
    },
    {
      id: 'product-pending-import',
      titleKey: 'productPendingImport',
      icon: Upload,
      descriptionKey: 'productPendingImport',
      route: '/product-overview/import-pending'
    }
    ,
    {
      id: 'product-3pl-import',
      titleKey: 'product3PLDataImport',
      icon: Upload,
      descriptionKey: 'product3PLDataImport',
      route: '/product-overview/import-3pl'
    }
  ];

  useEffect(() => {
    const email = localStorage.getItem('userEmail');
    const company = localStorage.getItem('selectedCompanyName');
    if (!email || !company) {
      navigate('/login');
      return;
    }
    setUserEmail(email);
    setCompanyName(company);
  }, [navigate]);

  const handleModuleClick = (module: ModuleItem) => {
    navigate(module.route);
  };

  const handleCategoryChange = (categoryId: string) => {
    // 如果点击的是当前分类（product），不需要导航
    if (categoryId === 'product') {
      return;
    }
    // 如果点击的是其他分类，导航到 Dashboard 并设置分类
    // 使用 localStorage 来传递分类信息
    localStorage.setItem('selectedCategory', categoryId);
    navigate(ROUTES.dashboard.root);
  };

  return (
    <div className="min-h-screen bg-background flex">
      <AutoHideSidebar 
        selectedCategory="product"
        setSelectedCategory={handleCategoryChange}
        userEmail={userEmail}
        companyName={companyName}
      />

      <div className="flex-1 flex flex-col">
        <div className="bg-card/50 border-b border-border p-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold bg-gradient-to-r from-purple-400 to-cyan-400 bg-clip-text text-transparent">產品總覽</h1>
              <p className="text-muted-foreground">管理產品資訊、庫存和統計數據</p>
            </div>
            <div className="text-right">
              <p className="text-foreground font-medium">{userEmail}</p>
              <p className="text-muted-foreground text-sm">{companyName}</p>
            </div>
          </div>
        </div>

        <div className="flex-1 p-6 bg-background">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-6 max-w-4xl mx-auto">
            {modules.map((module) => {
              const IconComponent = module.icon;
              
              return (
                <Card
                  key={module.id}
                  className="group cursor-pointer transition-all duration-300 hover:scale-105 bg-card/80 border-border hover:border-purple-500/50 hover:shadow-2xl hover:shadow-purple-500/20"
                  onClick={() => handleModuleClick(module)}
                >
                  <CardHeader className="pb-3">
                    <div className="flex items-center justify-between">
                      <div className="w-12 h-12 bg-gradient-to-br from-purple-600 to-cyan-600 rounded-xl flex items-center justify-center group-hover:shadow-lg group-hover:shadow-purple-500/30 transition-all duration-300">
                        <IconComponent className="w-6 h-6 text-white" />
                      </div>
                      {module.badge && (
                        <Badge variant="secondary" className="bg-purple-600/20 text-purple-400 border-purple-600/30">
                          {module.badge}
                        </Badge>
                      )}
                    </div>
                  </CardHeader>
                  <CardContent>
                    <CardTitle className="text-foreground text-lg mb-2 group-hover:text-purple-500 transition-colors">
                      {t(module.titleKey)}
                    </CardTitle>
                    <p className="text-muted-foreground text-sm">
                      {t(module.descriptionKey)}
                    </p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductOverview;
